<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">{{ $t("COMMON.INTERNAL_INVOICES") }}</h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            v-if="
              !hideAddButton &&
              $currentUserCan($permissions.PERM_CREATE_ESTABLISHMENT_INTERNAL_INVOICES)
            "
            @click="addTransfer()"
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("COMMON.ADD") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>

    <div>
      <div
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <el-select
          class="select-primary pagination-select"
          v-model="selectedStatus"
          :placeholder="$t('COMMON.STATUS')"
        >
          <el-option
            class="select-primary"
            :key="0"
            :label="$t('COMMON.STATUS')"
            :value="null"
          >
          </el-option>
          <el-option
            v-for="status in statuses"
            :key="status"
            :label="$t(`ESTABLISHMENT_INTERNAL_INVOICES.STATUS_${status}`)"
            :value="status"
            class="select-primary"
          />
        </el-select>

        <establishment-selector
          class="mr-2"
          :allOptionLabel="$t('COMMON.FILTER_ALL.SOURCES')"
          :establishment="selectedSourceEstablishment"
          :filterable="true"
          :showAll="true"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
          @establishmentChanged="onSourceEstablishmentChanged"
        />

        <establishment-selector
          class="mr-2"
          :allOptionLabel="$t('COMMON.FILTER_ALL.DESTINATIONS')"
          :establishment="selectedDestinationEstablishment"
          :filterable="true"
          :showAll="true"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
          @establishmentChanged="onDestinationEstablishmentChanged"
        />

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.NUMBER_OR_ESTABLISHMENT')"
            clearable
          />
        </div>
      </div>

      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="transfers"
        @sort-change="sortChange"
        v-loading="loading"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>

        <el-table-column
          :label="$t('COMMON.NUMBER')"
          prop="seq_number"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ row.seq_number ?? row.code }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('COMMON.SOURCE')" min-width="200px">
          <template v-slot="{ row }">
            {{ row.sourceEstablishment?.company_name || row.source_establishment_id }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('COMMON.DESTINATION')" min-width="200px">
          <template v-slot="{ row }">
            {{
              row.destinationEstablishment?.company_name ||
              row.destination_establishment_id
            }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENT_INTERNAL_INVOICES.INTERNAL_INVOICE_DATE')"
          prop="transfer_date"
          sortable="custom"
          min-width="150px"
        />
        <el-table-column
          :label="$t('COMMON.STATUS')"
          prop="status"
          min-width="150px"
        >
          <template v-slot="{ row }">
            <establishment-internal-invoice-status-badge :transfer="row" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.TOTAL')"
          prop="pricing.total"
          min-width="120px"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row?.pricing?.total || 0) }}
          </template>
        </el-table-column>
        <el-table-column min-width="160px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_ESTABLISHMENT_INTERNAL_INVOICES)"
            >
              <a
                type="text"
                @click="viewTransfer(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_ESTABLISHMENT_INTERNAL_INVOICES)"
            >
              <a
                type="text"
                @click="editTransfer(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>
            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="
                row.status === 'DRAFT' &&
                $currentUserCan($permissions.PERM_DELETE_ESTABLISHMENT_INTERNAL_INVOICES)
              "
            >
              <a
                type="text"
                @click="deleteTransfer(row)"
                class="table-action text-danger"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>

    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import {
  establishmentInternalInvoiceStatuses,
} from "@/constants/establishmentInternalInvoices";
import EstablishmentInternalInvoiceStatusBadge from "./EstablishmentInternalInvoiceStatusBadge.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";

export default {
  name: "establishment-internal-invoice-list-table",

  components: {
    BasePagination,
    EstablishmentInternalInvoiceStatusBadge,
    EstablishmentSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterEstablishment: {
      type: [String, Number],
      default: null,
    },
    filterSourceEstablishment: {
      type: [String, Number],
      default: null,
    },
    filterDestinationEstablishment: {
      type: [String, Number],
      default: null,
    },
    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      query: null,
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      transfers: [],
      loading: true,
      selectedStatus: null,
      statuses: establishmentInternalInvoiceStatuses,
      selectedSourceEstablishment: this.filterSourceEstablishment,
      selectedDestinationEstablishment: this.filterDestinationEstablishment,
      selectedEstablishment: this.filterEstablishment,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    selectedStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedSourceEstablishment: {
      handler: "getListDebounced",
    },
    selectedDestinationEstablishment: {
      handler: "getListDebounced",
    },
    filterEstablishment(value) {
      this.selectedEstablishment = value;
      this.getListDebounced();
    },
    filterSourceEstablishment(value) {
      this.selectedSourceEstablishment = value;
      this.getListDebounced();
    },
    filterDestinationEstablishment(value) {
      this.selectedDestinationEstablishment = value;
      this.getListDebounced();
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    onSourceEstablishmentChanged(establishmentId) {
      this.selectedSourceEstablishment = establishmentId;
    },

    onDestinationEstablishmentChanged(establishmentId) {
      this.selectedDestinationEstablishment = establishmentId;
    },

    async getList() {
      this.loading = true;
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            ...(this.selectedStatus ? { status: this.selectedStatus } : {}),
            ...(this.selectedEstablishment
              ? { establishment: this.selectedEstablishment }
              : {}),
            ...(this.selectedSourceEstablishment
              ? { source_establishment: this.selectedSourceEstablishment }
              : {}),
            ...(this.selectedDestinationEstablishment
              ? { destination_establishment: this.selectedDestinationEstablishment }
              : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "sourceEstablishment,destinationEstablishment",
        };

        await this.$store.dispatch("establishmentInternalInvoices/list", params);
        this.transfers = this.$store.getters["establishmentInternalInvoices/list"];
        this.total = this.$store.getters["establishmentInternalInvoices/listTotal"];
      } catch (error) {
        console.error(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    viewTransfer(row) {
      this.$router.push({
        name: "View EstablishmentInternalInvoice",
        params: { id: row.id },
      });
    },

    editTransfer(row) {
      this.$router.push({
        name: "Edit EstablishmentInternalInvoice",
        params: { id: row.id },
      });
    },

    addTransfer() {
      this.$router.push({ name: "Add EstablishmentInternalInvoice" });
    },

    async deleteTransfer(row) {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_INVOICES.DELETE_ESTABLISHMENT_INTERNAL_INVOICE"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      try {
        await this.$store.dispatch("establishmentInternalInvoices/destroy", {
          id: row.id,
        });
        await this.getList();
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENT_INTERNAL_INVOICES.DELETE_ESTABLISHMENT_INTERNAL_INVOICE"),
        });
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
